/**
 * Marketing illustrations — a small set of inline SVG primitives
 * used by the `/handoff/demo` landing (issue #435).
 *
 * Why inline React (not .svg files)?
 *   - Avoids an extra HTTP request for a 60×60 icon (the network
 *     round trip dominates the asset size).
 *   - Allows the illustration to inherit `currentColor` from CSS
 *     so the brand palette stays consistent across surfaces.
 *   - Keeps the `web/src/assets/marketing/` directory empty so we
 *     don't ship unused assets. If we later need to add animation
 *     (separate ticket), the components can be promoted to
 *     standalone .svg files without changing call sites.
 *
 * Brand palette used here (see `web/src/index.css`):
 *   - mint:    #4caf50  (--accent)
 *   - navy:    #0b1f2a  (--bg)
 *   - white:   #ffffff  (--fg-on-accent)
 *
 * All illustrations:
 *   - Use `currentColor` where possible so the parent can theme.
 *   - Default `aria-hidden="true"` — the surrounding section
 *     supplies the accessible name.
 */
import type { ReactElement } from 'react';

export const NAVY = '#0b1f2a';
export const MINT = '#4caf50';
export const MINT_SOFT = 'rgba(76, 175, 80, 0.18)';

/* ---------------- 60×60 process-step illustrations ---------------- */

/**
 * Step 1 — SLP sends a link.
 *   Envelope with a small speech-bubble tail to imply "sent to a
 *   parent". Mint envelope body, navy seal.
 */
export function SlpSendsLinkIllustration(): ReactElement {
  return (
    <svg
      width={60}
      height={60}
      viewBox="0 0 60 60"
      role="img"
      aria-label="SLP sends a link to a parent"
    >
      <rect
        x={6}
        y={14}
        width={48}
        height={32}
        rx={4}
        fill={MINT}
        stroke="none"
      />
      <path d="M6 18 L30 34 L54 18" fill="none" stroke={NAVY} strokeWidth={2} />
      <circle cx={48} cy={46} r={8} fill={NAVY} />
      <path
        d="M44 46 L47 49 L52 43"
        fill="none"
        stroke={MINT}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Step 2 — Kid practices on a device.
 *   A small tablet outline with a kid-friendly picture icon
 *   (a sun + a face). Navy bezel, mint screen.
 */
export function KidPracticesIllustration(): ReactElement {
  return (
    <svg
      width={60}
      height={60}
      viewBox="0 0 60 60"
      role="img"
      aria-label="Kid practices independently on a device"
    >
      <rect
        x={10}
        y={8}
        width={40}
        height={44}
        rx={4}
        fill={NAVY}
        stroke="none"
      />
      <rect
        x={14}
        y={14}
        width={32}
        height={28}
        rx={2}
        fill={MINT_SOFT}
        stroke="none"
      />
      <circle cx={30} cy={28} r={6} fill={MINT} />
      <line
        x1={30}
        y1={10}
        x2={30}
        y2={12}
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={28}
        y1={11}
        x2={32}
        y2={11}
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={28}
        y1={11}
        x2={28}
        y2={7}
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={32}
        y1={11}
        x2={32}
        y2={7}
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={28}
        y1={7}
        x2={32}
        y2={7}
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <rect x={20} y={44} width={20} height={4} rx={2} fill={MINT} />
    </svg>
  );
}

/**
 * Step 3 — Parent sees progress on a dashboard.
 *   A simplified dashboard card with a sparkline + two stat rows.
 *   Mint sparkline, navy card body.
 */
export function ParentSeesProgressIllustration(): ReactElement {
  return (
    <svg
      width={60}
      height={60}
      viewBox="0 0 60 60"
      role="img"
      aria-label="Parent sees progress on the dashboard"
    >
      <rect
        x={6}
        y={10}
        width={48}
        height={40}
        rx={4}
        fill={NAVY}
        stroke="none"
      />
      <rect x={10} y={14} width={20} height={3} rx={1.5} fill={MINT} />
      <rect x={10} y={21} width={14} height={2} rx={1} fill="rgba(255,255,255,0.4)" />
      <rect x={10} y={26} width={18} height={2} rx={1} fill="rgba(255,255,255,0.4)" />
      <polyline
        fill="none"
        stroke={MINT}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        points="10 44 18 38 26 40 34 32 42 36 50 30"
      />
      <circle cx={42} cy={36} r={2} fill={MINT} />
    </svg>
  );
}

/* ---------------- 160×90 "what you'll see" preview thumbnails ---------------- */

/**
 * Preview thumbnail — abstract picture-naming trial UI.
 *   A small frame with a placeholder image (a sun shape), four
 *   answer chips below. Mint chips, navy frame. Designed to read
 *   as an illustration, NOT a screenshot.
 */
export function PracticePreviewIllustration(): ReactElement {
  return (
    <svg
      width={160}
      height={90}
      viewBox="0 0 160 90"
      role="img"
      aria-label="Preview of a picture-naming trial UI"
    >
      <rect x={0} y={0} width={160} height={90} rx={6} fill={NAVY} />
      <rect
        x={10}
        y={10}
        width={140}
        height={36}
        rx={3}
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(255,255,255,0.12)"
      />
      <circle cx={80} cy={28} r={10} fill={MINT} />
      <line
        x1={80}
        y1={14}
        x2={80}
        y2={17}
        stroke={NAVY}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={76}
        y1={15.5}
        x2={84}
        y2={15.5}
        stroke={NAVY}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={76}
        y1={15.5}
        x2={76}
        y2={12}
        stroke={NAVY}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={84}
        y1={15.5}
        x2={84}
        y2={12}
        stroke={NAVY}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1={76}
        y1={12}
        x2={84}
        y2={12}
        stroke={NAVY}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <rect x={10} y={54} width={32} height={12} rx={6} fill={MINT} />
      <rect
        x={46}
        y={54}
        width={32}
        height={12}
        rx={6}
        fill="rgba(255,255,255,0.08)"
      />
      <rect
        x={82}
        y={54}
        width={32}
        height={12}
        rx={6}
        fill="rgba(255,255,255,0.08)"
      />
      <rect
        x={118}
        y={54}
        width={32}
        height={12}
        rx={6}
        fill="rgba(255,255,255,0.08)"
      />
      <rect
        x={10}
        y={72}
        width={48}
        height={6}
        rx={3}
        fill="rgba(255,255,255,0.12)"
      />
      <rect
        x={10}
        y={80}
        width={32}
        height={4}
        rx={2}
        fill="rgba(255,255,255,0.08)"
      />
    </svg>
  );
}

/**
 * Preview thumbnail — abstract parent dashboard UI.
 *   Two stat cards on top, a sparkline chart below, and a
 *   message row at the bottom. Mint data + accents, navy canvas.
 */
export function DashboardPreviewIllustration(): ReactElement {
  return (
    <svg
      width={160}
      height={90}
      viewBox="0 0 160 90"
      role="img"
      aria-label="Preview of the parent dashboard UI"
    >
      <rect x={0} y={0} width={160} height={90} rx={6} fill={NAVY} />
      <rect
        x={8}
        y={8}
        width={68}
        height={26}
        rx={3}
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(255,255,255,0.12)"
      />
      <rect
        x={84}
        y={8}
        width={68}
        height={26}
        rx={3}
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(255,255,255,0.12)"
      />
      <rect x={14} y={14} width={26} height={3} rx={1.5} fill={MINT} />
      <rect
        x={14}
        y={22}
        width={36}
        height={6}
        rx={1}
        fill="rgba(255,255,255,0.5)"
      />
      <rect x={90} y={14} width={26} height={3} rx={1.5} fill={MINT} />
      <rect
        x={90}
        y={22}
        width={36}
        height={6}
        rx={1}
        fill="rgba(255,255,255,0.5)"
      />
      <rect
        x={8}
        y={40}
        width={144}
        height={32}
        rx={3}
        fill="rgba(255,255,255,0.04)"
        stroke="rgba(255,255,255,0.08)"
      />
      <polyline
        fill="none"
        stroke={MINT}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        points="14 64 24 58 34 60 44 52 54 56 64 50 74 54 84 48 94 52 104 46 114 50 124 44 134 48 144 42"
      />
      <rect
        x={8}
        y={78}
        width={144}
        height={6}
        rx={3}
        fill="rgba(255,255,255,0.08)"
      />
    </svg>
  );
}

/**
 * Lock icon — 40×40 trust glyph for the "Zero data sent to any
 * server" preview card. Mint shackle + navy body so it pops on
 * the navy canvas.
 */
export function LockIconIllustration(): ReactElement {
  return (
    <svg
      width={40}
      height={40}
      viewBox="0 0 40 40"
      role="img"
      aria-label="Lock icon — zero data sent"
    >
      <path
        d="M14 18 V14 a6 6 0 0 1 12 0 V18"
        fill="none"
        stroke={MINT}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <rect
        x={9}
        y={18}
        width={22}
        height={16}
        rx={3}
        fill={NAVY}
        stroke={MINT}
        strokeWidth={2}
      />
      <circle cx={20} cy={25} r={2} fill={MINT} />
      <rect x={19} y={25} width={2} height={5} rx={1} fill={MINT} />
    </svg>
  );
}